import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Search, Plus, Edit, Trash2, Eye, CheckCircle, MessageSquare, Calendar, Paperclip, FileIcon, Download, X,
  UsersRound, CheckSquare, Repeat, AlertTriangle, Clock, Shield, TrendingUp, Zap, Link2, Ban,
  ArrowRight, Loader2, Target, BarChart3, Users, Lightbulb, Columns3, GripVertical,
} from 'lucide-react';
import { format } from 'date-fns';
import AttachmentAnnotator from '@/components/AttachmentAnnotator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { value: 'todo', label: 'To Do', color: '#FF9500', bg: 'bg-[#FF9500]' },
  { value: 'in_progress', label: 'In Progress', color: '#007AFF', bg: 'bg-[#007AFF]' },
  { value: 'review', label: 'Review', color: '#AF52DE', bg: 'bg-[#AF52DE]' },
  { value: 'blocked', label: 'Blocked', color: '#FF3B30', bg: 'bg-[#FF3B30]' },
  { value: 'done', label: 'Done', color: '#34C759', bg: 'bg-[#34C759]' },
];

const TasksPage = () => {
  const { token, user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isNoteOpen, setIsNoteOpen] = useState(false);
  const [isCompleteOpen, setIsCompleteOpen] = useState(false);
  const [completionSummary, setCompletionSummary] = useState('');
  const [completingTaskId, setCompletingTaskId] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [noteContent, setNoteContent] = useState('');
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    title: '', description: '', priority: 'medium', due_date: '', assigned_to: '', status: 'todo',
    is_recurring: false, recurrence_frequency: '', recurrence_end_date: '',
    depends_on: [], blocked_by_person: '', blocker_reason: '',
  });
  const [createFiles, setCreateFiles] = useState([]);
  const createFileRef = useRef(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamFilter, setTeamFilter] = useState('');
  const [workflowReport, setWorkflowReport] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [depInput, setDepInput] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [swimlaneMode, setSwimlaneMode] = useState('none'); // none, assignee, priority
  const [reviewingAttachment, setReviewingAttachment] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchTasks(); fetchUsers(); fetchTeams(); }, [search, priorityFilter, statusFilter, teamFilter]);

  const fetchTeams = async () => { try { const res = await axios.get(`${API}/teams`, { headers }); setTeams(res.data); } catch {} };

  const fetchTasks = async () => {
    try {
      const params = { search, priority: priorityFilter, status: statusFilter };
      if (teamFilter) params.team_id = teamFilter;
      const res = await axios.get(`${API}/tasks`, { params, headers });
      setTasks(res.data);
      setSelectedIds([]);
    } catch { toast.error('Failed to fetch tasks'); }
    finally { setLoading(false); }
  };

  const fetchUsers = async () => { try { const res = await axios.get(`${API}/users`, { headers }); setUsers(res.data); } catch {} };

  const fetchWorkflowReport = async () => {
    try {
      const [rpt, pred] = await Promise.all([
        axios.get(`${API}/tasks/workflow-report`, { headers }),
        axios.get(`${API}/tasks/predictions`, { headers }),
      ]);
      setWorkflowReport(rpt.data);
      setPredictions(pred.data);
    } catch { toast.error('Failed to fetch workflow report'); }
  };

  const handleCreate = async () => {
    if (!form.title) { toast.error('Title is required'); return; }
    try {
      const res = await axios.post(`${API}/tasks`, form, { headers });
      const newTaskId = res.data.task_id;
      // Upload pending files
      if (createFiles.length > 0) {
        for (const file of createFiles) {
          const fd = new FormData();
          fd.append('file', file);
          try {
            await axios.post(`${API}/tasks/${newTaskId}/attachments`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
          } catch { /* continue with other files */ }
        }
        toast.success(`Task created with ${createFiles.length} attachment(s)`);
      } else {
        toast.success('Task created');
      }
      setIsCreateOpen(false);
      setForm({ title: '', description: '', priority: 'medium', due_date: '', assigned_to: '', status: 'todo', is_recurring: false, recurrence_frequency: '', recurrence_end_date: '', depends_on: [], blocked_by_person: '', blocker_reason: '' });
      setCreateFiles([]);
      fetchTasks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create task'); }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/tasks/${selectedTask.task_id}`, form, { headers });
      toast.success('Task updated');
      setIsEditOpen(false);
      fetchTasks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update task'); }
  };

  const handleDelete = async (taskId) => {
    if (!confirm('Are you sure?')) return;
    try { await axios.delete(`${API}/tasks/${taskId}`, { headers }); toast.success('Task deleted'); fetchTasks(); }
    catch { toast.error('Failed to delete task'); }
  };

  const handleQuickStatus = async (taskId, newStatus) => {
    if (newStatus === 'done') {
      setCompletingTaskId(taskId);
      setCompletionSummary('');
      setIsCompleteOpen(true);
      return;
    }
    setUpdatingStatus(taskId);
    try {
      const payload = { status: newStatus };
      if (newStatus === 'blocked') {
        payload.blocker_reason = 'Needs investigation';
      }
      await axios.put(`${API}/tasks/${taskId}`, payload, { headers });
      toast.success(`Status → ${STATUSES.find(s => s.value === newStatus)?.label}`);
      fetchTasks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setUpdatingStatus(null); }
  };

  const handleComplete = async () => {
    if (!completionSummary.trim()) { toast.error('Completion summary is required'); return; }
    try {
      await axios.post(`${API}/tasks/${completingTaskId}/end`, { completion_summary: completionSummary }, { headers });
      toast.success('Task completed');
      setIsCompleteOpen(false);
      setCompletionSummary('');
      fetchTasks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleAddNote = async () => {
    if (!noteContent) return;
    try {
      await axios.post(`${API}/tasks/${selectedTask.task_id}/notes`, { content: noteContent }, { headers });
      toast.success('Note added'); setNoteContent(''); setIsNoteOpen(false); fetchTasks();
    } catch { toast.error('Failed to add note'); }
  };

  const handleAddDep = async (taskId, depId) => {
    try {
      const res = await axios.post(`${API}/tasks/${taskId}/dependencies`, { depends_on_id: depId }, { headers });
      toast.success(res.data.auto_blocked ? 'Dependency added — task auto-blocked' : 'Dependency added');
      fetchTasks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleRemoveDep = async (taskId, depId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}/dependencies/${depId}`, { headers });
      toast.success('Dependency removed'); fetchTasks();
    } catch { toast.error('Failed'); }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length || !confirm(`Delete ${selectedIds.length} task(s)?`)) return;
    try { await axios.post(`${API}/tasks/bulk/delete`, { task_ids: selectedIds }, { headers }); toast.success(`${selectedIds.length} tasks deleted`); setSelectedIds([]); fetchTasks(); }
    catch { toast.error('Bulk delete failed'); }
  };

  const handleExport = async (fmt) => {
    const params = new URLSearchParams({ format: fmt });
    if (teamFilter) params.set('team_id', teamFilter);
    try {
      const res = await axios.get(`${API}/export/tasks?${params.toString()}`, { headers, responseType: 'blob' });
      const mimeTypes = { pdf: 'application/pdf', csv: 'text/csv', json: 'application/json' };
      const blob = new Blob([res.data], { type: mimeTypes[fmt] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tasks-export.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const toggleSelect = (taskId) => setSelectedIds(prev => prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]);
  const toggleSelectAll = () => { if (selectedIds.length === tasks.length) setSelectedIds([]); else setSelectedIds(tasks.map(t => t.task_id)); };

  const handleAttachToTask = (taskId) => {
    fileInputRef.current?.click();
    fileInputRef.current.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) { toast.error('File too large. Max 10MB.'); return; }
      const formData = new FormData();
      formData.append('file', file);
      try {
        await axios.post(`${API}/tasks/${taskId}/attachments`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
        toast.success('File attached'); fetchTasks();
        if (selectedTask?.task_id === taskId) { const res = await axios.get(`${API}/tasks/${taskId}`, { headers }); setSelectedTask(res.data); }
      } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
      finally { e.target.value = ''; }
    };
  };

  const handleRemoveAttachment = async (taskId, fileId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}/attachments/${fileId}`, { headers });
      toast.success('Attachment removed'); fetchTasks();
      if (selectedTask?.task_id === taskId) { setSelectedTask(prev => ({ ...prev, attachments: prev.attachments.filter(a => a.file_id !== fileId) })); }
    } catch { toast.error('Failed to remove'); }
  };

  const handleKanbanDrop = async (taskId, newStatus) => {
    const task = tasks.find(t => t.task_id === taskId);
    if (!task || task.status === newStatus) { setDraggedTask(null); setDragOverCol(null); return; }
    if (newStatus === 'done') {
      setCompletingTaskId(taskId);
      setCompletionSummary('');
      setIsCompleteOpen(true);
      setDraggedTask(null);
      setDragOverCol(null);
      return;
    }
    try {
      const payload = { status: newStatus };
      if (newStatus === 'blocked') payload.blocker_reason = 'Needs investigation';
      await axios.put(`${API}/tasks/${taskId}`, payload, { headers });
      toast.success(`Moved to ${STATUSES.find(s => s.value === newStatus)?.label}`);
      fetchTasks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Move failed'); }
    finally { setDraggedTask(null); setDragOverCol(null); }
  };

  const openEdit = (task) => {
    setSelectedTask(task);
    const normalizedStatus = task.status === 'pending' ? 'todo' : task.status === 'completed' ? 'done' : task.status;
    setForm({
      title: task.title, description: task.description || '', priority: task.priority,
      due_date: task.due_date || '', assigned_to: task.assigned_to || '', status: normalizedStatus || 'todo',
      is_recurring: task.is_recurring || false, recurrence_frequency: task.recurrence_frequency || '',
      recurrence_end_date: task.recurrence_end_date || '', depends_on: task.depends_on || [],
      blocked_by_person: task.blocked_by_person || '', blocker_reason: task.blocker_reason || '',
      completion_summary: task.completion_summary || '',
    });
    setIsEditOpen(true);
  };

  const getPriorityBadge = (p) => ({ critical: 'badge-critical', high: 'badge-high', medium: 'badge-medium', low: 'badge-low' }[p] || 'badge-low');
  const getStatusInfo = (s) => STATUSES.find(st => st.value === s) || STATUSES[0];
  const getUserName = (uid) => users.find(u => u.user_id === uid)?.name || uid || 'Unassigned';

  const riskBadge = (task) => {
    const badges = [];
    if (task.is_overdue) badges.push(<Badge key="o" className="text-[8px] bg-[#FF3B30]/10 text-[#FF3B30] ml-1">OVERDUE</Badge>);
    else if (task.is_at_risk) badges.push(<Badge key="r" className="text-[8px] bg-[#FF9500]/10 text-[#FF9500] ml-1">AT RISK</Badge>);
    return badges;
  };

  const nextStatuses = (current) => {
    const flow = {
      todo: ['in_progress'],
      in_progress: ['review', 'blocked', 'done'],
      review: ['done', 'in_progress'],
      blocked: ['todo', 'in_progress'],
      done: [],
    };
    return flow[current] || [];
  };

  return (
    <div data-testid="tasks-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">Task Workflow</h1>
          <p className="text-[#4B5563] text-sm">What to work on, what's blocked, what's at risk</p>
        </div>
        <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={() => setIsCreateOpen(true)} data-testid="create-task-btn">
          <Plus className="w-4 h-4 mr-2" />New Task
        </Button>
      </div>

      <Tabs defaultValue="tasks" onValueChange={(v) => { if (v === 'workflow') fetchWorkflowReport(); }}>
        <TabsList className="w-full justify-start mb-4">
          <TabsTrigger value="tasks" data-testid="tab-tasks"><CheckSquare className="w-3.5 h-3.5 mr-1" />All Tasks</TabsTrigger>
          <TabsTrigger value="kanban" data-testid="tab-kanban"><Columns3 className="w-3.5 h-3.5 mr-1" />Kanban Board</TabsTrigger>
          <TabsTrigger value="workflow" data-testid="tab-workflow"><Shield className="w-3.5 h-3.5 mr-1" />Workflow Report</TabsTrigger>
        </TabsList>

        {/* ==================== ALL TASKS TAB ==================== */}
        <TabsContent value="tasks">
          {/* Filters */}
          <Card className="border-[#E5E7EB] mb-4">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                  <Input placeholder="Search tasks..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="task-search-input" />
                </div>
                <Select value={priorityFilter || "all"} onValueChange={(v) => setPriorityFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[140px]" data-testid="priority-filter"><SelectValue placeholder="Priority" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priority</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[140px]" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={teamFilter || "all"} onValueChange={(v) => setTeamFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[140px]" data-testid="team-filter"><SelectValue placeholder="Team" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Teams</SelectItem>
                    {teams.map(t => <SelectItem key={t.team_id} value={t.team_id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex gap-1 ml-auto">
                  <Button variant="outline" size="sm" onClick={() => handleExport('csv')} data-testid="export-csv-btn"><Download className="w-3.5 h-3.5 mr-1" />CSV</Button>
                  <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} data-testid="export-pdf-btn"><Download className="w-3.5 h-3.5 mr-1" />PDF</Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bulk Actions */}
          {selectedIds.length > 0 && (
            <Card className="border-[#002FA7] bg-[#002FA7]/5 mb-4" data-testid="bulk-actions-bar">
              <CardContent className="p-3 flex items-center gap-3">
                <span className="text-sm font-medium">{selectedIds.length} selected</span>
                <div className="flex gap-2 ml-auto">
                  <Button size="sm" variant="outline" className="text-[#FF3B30] border-[#FF3B30]" onClick={handleBulkDelete} data-testid="bulk-delete-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])}><X className="w-3.5 h-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tasks Table */}
          <Card className="border-[#E5E7EB]">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>
              ) : (
                <Table data-testid="tasks-table">
                  <TableHeader>
                    <TableRow className="bg-[#F3F4F6]">
                      <TableHead className="w-10"><input type="checkbox" checked={selectedIds.length === tasks.length && tasks.length > 0} onChange={toggleSelectAll} className="rounded border-[#D1D5DB]" data-testid="select-all-tasks" /></TableHead>
                      <TableHead>Task</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Assignee</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Quick Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tasks.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#9CA3AF]">No tasks found</TableCell></TableRow>
                    ) : tasks.map((task) => {
                      const si = getStatusInfo(task.status);
                      return (
                        <TableRow key={task.task_id} data-testid={`task-row-${task.task_id}`} className={`${selectedIds.includes(task.task_id) ? 'bg-[#002FA7]/5' : ''} ${task.is_overdue ? 'bg-[#FF3B30]/[0.03]' : task.is_at_risk ? 'bg-[#FF9500]/[0.03]' : ''}`}>
                          <TableCell><input type="checkbox" checked={selectedIds.includes(task.task_id)} onChange={() => toggleSelect(task.task_id)} className="rounded border-[#D1D5DB]" /></TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              <button className="font-medium text-left hover:text-[#002FA7] transition-colors" onClick={() => { setSelectedTask(task); setIsViewOpen(true); }}>{task.title}</button>
                              {task.is_recurring && <Badge className="text-[8px] bg-[#002FA7]/10 text-[#002FA7]" data-testid={`recurring-badge-${task.task_id}`}><Repeat className="w-2.5 h-2.5" /></Badge>}
                              {task.depends_on?.length > 0 && <Badge className="text-[8px] bg-[#5856D6]/10 text-[#5856D6]"><Link2 className="w-2.5 h-2.5" /></Badge>}
                              {task.attachments?.length > 0 && <Badge className="text-[8px] bg-[#007AFF]/10 text-[#007AFF]"><Paperclip className="w-2.5 h-2.5 mr-0.5" />{task.attachments.length}</Badge>}
                              {riskBadge(task)}
                            </div>
                            {task.suggested_action && <p className="text-[10px] text-[#FF9500] mt-0.5 flex items-center gap-1"><Lightbulb className="w-2.5 h-2.5" />{task.suggested_action}</p>}
                          </TableCell>
                          <TableCell><Badge className={getPriorityBadge(task.priority)}>{task.priority}</Badge></TableCell>
                          <TableCell><Badge className={`${si.bg} text-white`}>{si.label}</Badge></TableCell>
                          <TableCell className="text-xs">{getUserName(task.assigned_to)}</TableCell>
                          <TableCell className="text-xs">{task.due_date ? format(new Date(task.due_date), 'MMM d, yyyy') : '-'}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {nextStatuses(task.status).map(ns => {
                                const nsi = getStatusInfo(ns);
                                return (
                                  <Button key={ns} size="sm" variant="outline" className="h-6 text-[9px] px-1.5" style={{ borderColor: nsi.color + '50', color: nsi.color }}
                                    onClick={() => handleQuickStatus(task.task_id, ns)}
                                    disabled={updatingStatus === task.task_id}
                                    data-testid={`quick-${ns}-${task.task_id}`}>
                                    {updatingStatus === task.task_id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <ArrowRight className="w-2.5 h-2.5 mr-0.5" />}
                                    {nsi.label}
                                  </Button>
                                );
                              })}
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openEdit(task)} data-testid={`edit-task-${task.task_id}`}><Edit className="w-3 h-3" /></Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setSelectedTask(task); setIsNoteOpen(true); }} data-testid={`note-task-${task.task_id}`}><MessageSquare className="w-3 h-3" /></Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-[#FF3B30]" onClick={() => handleDelete(task.task_id)} data-testid={`delete-task-${task.task_id}`}><Trash2 className="w-3 h-3" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== KANBAN BOARD TAB ==================== */}
        <TabsContent value="kanban">
          {/* Swimlane Controls */}
          <div className="flex items-center justify-between mb-3" data-testid="kanban-controls">
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#4B5563]">Group by:</span>
              <div className="flex rounded-lg border border-[#E5E7EB] overflow-hidden">
                {[
                  { value: 'none', label: 'None' },
                  { value: 'assignee', label: 'Assignee' },
                  { value: 'priority', label: 'Priority' },
                ].map((opt) => (
                  <button key={opt.value}
                    onClick={() => setSwimlaneMode(opt.value)}
                    className={`px-3 py-1.5 text-[10px] font-medium transition-colors ${swimlaneMode === opt.value ? 'bg-[#002FA7] text-white' : 'bg-white text-[#4B5563] hover:bg-[#F3F4F6]'}`}
                    data-testid={`swimlane-${opt.value}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <span className="text-[10px] text-[#9CA3AF]">{tasks.length} tasks</span>
          </div>

          {swimlaneMode === 'none' ? (
            /* ---- Standard Kanban (no swimlanes) ---- */
            <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: 'calc(100vh - 280px)' }} data-testid="kanban-board">
              {STATUSES.map((col) => {
                const colTasks = tasks.filter(t => t.status === col.value);
                return <KanbanColumn key={col.value} col={col} colTasks={colTasks} isOver={dragOverCol === col.value} setDragOverCol={setDragOverCol} handleKanbanDrop={handleKanbanDrop} draggedTask={draggedTask} setDraggedTask={setDraggedTask} getPriorityBadge={getPriorityBadge} getUserName={getUserName} setSelectedTask={setSelectedTask} setIsViewOpen={setIsViewOpen} />;
              })}
            </div>
          ) : (
            /* ---- Swimlane Kanban ---- */
            <div className="space-y-4 overflow-x-auto pb-4" data-testid="kanban-board-swimlanes">
              {(() => {
                const lanes = swimlaneMode === 'assignee'
                  ? [...new Set(tasks.map(t => t.assigned_to || ''))].sort().map(uid => ({ key: uid, label: getUserName(uid), color: '#002FA7' }))
                  : [
                      { key: 'critical', label: 'Critical', color: '#FF3B30' },
                      { key: 'high', label: 'High', color: '#FF9500' },
                      { key: 'medium', label: 'Medium', color: '#007AFF' },
                      { key: 'low', label: 'Low', color: '#34C759' },
                    ];
                return lanes.map((lane) => {
                  const laneTasks = swimlaneMode === 'assignee'
                    ? tasks.filter(t => (t.assigned_to || '') === lane.key)
                    : tasks.filter(t => t.priority === lane.key);
                  if (laneTasks.length === 0) return null;
                  return (
                    <div key={lane.key} data-testid={`swimlane-${lane.key || 'unassigned'}`}>
                      {/* Swimlane Header */}
                      <div className="flex items-center gap-2 mb-2 px-1">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: lane.color }} />
                        <span className="text-xs font-semibold text-[#0A0A0A]">{lane.label}</span>
                        <Badge variant="secondary" className="text-[9px] h-4">{laneTasks.length}</Badge>
                        <div className="flex-1 h-px bg-[#E5E7EB]" />
                      </div>
                      {/* Columns for this lane */}
                      <div className="flex gap-3 overflow-x-auto">
                        {STATUSES.map((col) => {
                          const colTasks = laneTasks.filter(t => t.status === col.value);
                          return <KanbanColumn key={col.value} col={col} colTasks={colTasks} isOver={dragOverCol === `${lane.key}_${col.value}`} setDragOverCol={(v) => setDragOverCol(v ? `${lane.key}_${col.value}` : null)} handleKanbanDrop={handleKanbanDrop} draggedTask={draggedTask} setDraggedTask={setDraggedTask} getPriorityBadge={getPriorityBadge} getUserName={getUserName} setSelectedTask={setSelectedTask} setIsViewOpen={setIsViewOpen} compact />;
                        })}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </TabsContent>

        {/* ==================== WORKFLOW REPORT TAB ==================== */}
        <TabsContent value="workflow" className="space-y-4">
          {!workflowReport ? (
            <Card className="border-[#E5E7EB]"><CardContent className="p-12 text-center text-[#9CA3AF]">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" />
              <p className="text-sm">Loading workflow report...</p>
            </CardContent></Card>
          ) : (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="workflow-summary">
                {[
                  { label: 'Active Tasks', value: workflowReport.summary.total_active, icon: CheckSquare, color: '#007AFF' },
                  { label: 'Overdue', value: workflowReport.summary.overdue_count, icon: AlertTriangle, color: workflowReport.summary.overdue_count > 0 ? '#FF3B30' : '#34C759' },
                  { label: 'Blocked', value: workflowReport.summary.blocked_count, icon: Ban, color: workflowReport.summary.blocked_count > 0 ? '#FF3B30' : '#34C759' },
                  { label: 'At Risk', value: workflowReport.summary.at_risk_count, icon: Clock, color: workflowReport.summary.at_risk_count > 0 ? '#FF9500' : '#34C759' },
                  { label: 'Critical Not Started', value: workflowReport.summary.critical_not_started, icon: Target, color: workflowReport.summary.critical_not_started > 0 ? '#FF3B30' : '#34C759' },
                ].map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <Card key={i} className="border-[#E5E7EB]" data-testid={`workflow-stat-${i}`}>
                      <CardContent className="p-3 text-center">
                        <Icon className="w-4 h-4 mx-auto mb-1" style={{ color: s.color }} />
                        <p className="text-xl font-bold" style={{ color: s.color }}>{s.value}</p>
                        <p className="text-[10px] text-[#9CA3AF]">{s.label}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Overdue by Assignee */}
                <Card className="border-[#E5E7EB]" data-testid="overdue-section">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#FF3B30]" />Overdue Tasks by Assignee</CardTitle></CardHeader>
                  <CardContent>
                    {Object.keys(workflowReport.overdue_by_assignee).length === 0 ? (
                      <p className="text-xs text-[#34C759] text-center py-4">No overdue tasks</p>
                    ) : Object.entries(workflowReport.overdue_by_assignee).map(([name, tasks_list]) => (
                      <div key={name} className="mb-3">
                        <p className="text-xs font-semibold text-[#0A0A0A] mb-1 flex items-center gap-1"><Users className="w-3 h-3" />{name} ({tasks_list.length})</p>
                        {tasks_list.map((t, i) => (
                          <div key={i} className="p-2 mb-1 rounded border border-[#FF3B30]/20 bg-[#FF3B30]/[0.02]" data-testid={`overdue-task-${t.task_id}`}>
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium">{t.title}</span>
                              <Badge className={getPriorityBadge(t.priority)} style={{ fontSize: '9px' }}>{t.priority}</Badge>
                            </div>
                            {t.suggested_action && <p className="text-[10px] text-[#FF9500] mt-1 flex items-center gap-1"><Lightbulb className="w-2.5 h-2.5" />{t.suggested_action}</p>}
                          </div>
                        ))}
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Blocked Tasks */}
                <Card className="border-[#E5E7EB]" data-testid="blocked-section">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Ban className="w-4 h-4 text-[#FF3B30]" />Blocked Tasks & Blockers</CardTitle></CardHeader>
                  <CardContent>
                    {workflowReport.blocked_tasks.length === 0 ? (
                      <p className="text-xs text-[#34C759] text-center py-4">No blocked tasks</p>
                    ) : workflowReport.blocked_tasks.map((t, i) => (
                      <div key={i} className="p-2 mb-1.5 rounded border border-[#FF3B30]/20 bg-[#FF3B30]/[0.02]" data-testid={`blocked-task-${t.task_id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium">{t.title}</span>
                          <Badge className={getPriorityBadge(t.priority)} style={{ fontSize: '9px' }}>{t.priority}</Badge>
                        </div>
                        <p className="text-[10px] text-[#4B5563]">Assignee: {t.assignee_name}</p>
                        {t.blocker_reason && <p className="text-[10px] text-[#FF3B30]">Blocker: {t.blocker_reason}</p>}
                        {t.blocked_by_person && <p className="text-[10px] text-[#4B5563]">Blocked by: {t.blocked_by_person}</p>}
                        {t.suggested_action && <p className="text-[10px] text-[#FF9500] mt-1 flex items-center gap-1"><Lightbulb className="w-2.5 h-2.5" />{t.suggested_action}</p>}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* At-Risk Tasks */}
                <Card className="border-[#E5E7EB]" data-testid="at-risk-section">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Clock className="w-4 h-4 text-[#FF9500]" />At-Risk Tasks</CardTitle></CardHeader>
                  <CardContent>
                    {workflowReport.at_risk_tasks.length === 0 ? (
                      <p className="text-xs text-[#34C759] text-center py-4">No at-risk tasks</p>
                    ) : workflowReport.at_risk_tasks.map((t, i) => (
                      <div key={i} className="p-2 mb-1.5 rounded border border-[#FF9500]/20 bg-[#FF9500]/[0.02]" data-testid={`at-risk-task-${t.task_id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium">{t.title}</span>
                          <div className="flex gap-1">
                            <Badge className={getPriorityBadge(t.priority)} style={{ fontSize: '9px' }}>{t.priority}</Badge>
                            <Badge className={`${getStatusInfo(t.status).bg} text-white`} style={{ fontSize: '9px' }}>{getStatusInfo(t.status).label}</Badge>
                          </div>
                        </div>
                        <p className="text-[10px] text-[#4B5563]">{t.risk_reasons?.join(', ')}</p>
                        {t.suggested_action && <p className="text-[10px] text-[#FF9500] mt-1 flex items-center gap-1"><Lightbulb className="w-2.5 h-2.5" />{t.suggested_action}</p>}
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Top 3 Critical Not Started */}
                <Card className="border-[#E5E7EB]" data-testid="critical-section">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Target className="w-4 h-4 text-[#FF3B30]" />Top 3 Critical — Not Started</CardTitle></CardHeader>
                  <CardContent>
                    {workflowReport.top_critical_not_started.length === 0 ? (
                      <p className="text-xs text-[#34C759] text-center py-4">No unstarted critical tasks</p>
                    ) : workflowReport.top_critical_not_started.map((t, i) => (
                      <div key={i} className="p-2 mb-1.5 rounded border border-[#FF3B30]/20 bg-[#FF3B30]/[0.02]" data-testid={`critical-task-${i}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium">{t.title}</span>
                          <span className="text-[10px] text-[#9CA3AF]">{t.due_date || 'No due date'}</span>
                        </div>
                        <p className="text-[10px] text-[#4B5563]">Assignee: {t.assignee_name}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>

              {/* Prescriptive Actions */}
              {workflowReport.prescriptive_actions.length > 0 && (
                <Card className="border-[#E5E7EB]" data-testid="prescriptive-section">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Zap className="w-4 h-4 text-[#002FA7]" />Suggested Next Actions</CardTitle></CardHeader>
                  <CardContent className="space-y-1.5">
                    {workflowReport.prescriptive_actions.map((a, i) => (
                      <div key={i} className="p-2 rounded border-l-[3px] border border-[#E5E7EB]" style={{ borderLeftColor: a.priority === 'critical' ? '#FF3B30' : a.priority === 'high' ? '#FF9500' : '#007AFF' }} data-testid={`suggestion-${i}`}>
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-xs font-medium">{a.title}</span>
                          <div className="flex gap-1">
                            <Badge className={getPriorityBadge(a.priority)} style={{ fontSize: '9px' }}>{a.priority}</Badge>
                            <Badge className={`${getStatusInfo(a.status).bg} text-white`} style={{ fontSize: '9px' }}>{getStatusInfo(a.status).label}</Badge>
                          </div>
                        </div>
                        <p className="text-[10px] text-[#002FA7] flex items-center gap-1"><Lightbulb className="w-2.5 h-2.5" />{a.action}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Predictions */}
              {predictions.length > 0 && (
                <Card className="border-[#E5E7EB]" data-testid="predictions-section">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#5856D6]" />Predicted Completion Rates (Next 5 Days)</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {predictions.map((p, i) => {
                        const rateColor = p.predicted_completion_rate >= 70 ? '#34C759' : p.predicted_completion_rate >= 40 ? '#FF9500' : '#FF3B30';
                        return (
                          <div key={i} className="p-3 rounded-lg border border-[#E5E7EB] bg-white" data-testid={`prediction-${i}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-semibold text-[#0A0A0A]">{p.name}</span>
                              <span className="text-sm font-bold" style={{ color: rateColor }}>{p.predicted_completion_rate}%</span>
                            </div>
                            <div className="w-full h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden mb-2">
                              <div className="h-full rounded-full transition-all" style={{ width: `${p.predicted_completion_rate}%`, backgroundColor: rateColor }} />
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-center">
                              <div><p className="text-xs font-bold text-[#0A0A0A]">{p.active_tasks}</p><p className="text-[9px] text-[#9CA3AF]">Active</p></div>
                              <div><p className="text-xs font-bold text-[#0A0A0A]">{p.predicted_completable_5d}</p><p className="text-[9px] text-[#9CA3AF]">Can Complete</p></div>
                              <div><p className="text-xs font-bold text-[#0A0A0A]">{p.due_within_5d}</p><p className="text-[9px] text-[#9CA3AF]">Due Soon</p></div>
                            </div>
                            <p className="text-[9px] text-[#9CA3AF] mt-1 text-center">Avg cycle: {p.avg_cycle_days}d | Total done: {p.completed_total}</p>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Team Summary Paragraph */}
              <Card className="border-[#E5E7EB]" data-testid="team-summary-card">
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#002FA7]" />Team Summary</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-sm text-[#4B5563] leading-relaxed bg-[#F9FAFB] p-4 rounded-lg border border-[#E5E7EB]">
                    Your team currently has <strong>{workflowReport.summary.total_active} active tasks</strong>.
                    {workflowReport.summary.overdue_count > 0 ? ` ${workflowReport.summary.overdue_count} task(s) are overdue and need immediate attention.` : ' No tasks are overdue.'}
                    {workflowReport.summary.blocked_count > 0 ? ` ${workflowReport.summary.blocked_count} task(s) are blocked — review blockers and escalate if needed.` : ''}
                    {workflowReport.summary.at_risk_count > 0 ? ` ${workflowReport.summary.at_risk_count} task(s) are at risk of missing deadlines.` : ''}
                    {workflowReport.summary.critical_not_started > 0 ? ` ${workflowReport.summary.critical_not_started} critical task(s) haven't been started yet — prioritize these immediately.` : ''}
                    {workflowReport.summary.overdue_count === 0 && workflowReport.summary.blocked_count === 0 && workflowReport.summary.at_risk_count === 0 ? ' All tasks are on track — keep up the momentum.' : ' Focus on resolving blockers and overdue items first before taking on new work.'}
                  </p>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* ==================== DIALOGS ==================== */}

      {/* Create Task */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="create-task-dialog">
          <DialogHeader><DialogTitle>Create Task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-description-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-due-date-input" /></div>
            </div>
            <div>
              <Label>Assign To (Primary Assignee)</Label>
              <Select value={form.assigned_to || "none"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Select assignee" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Attachments */}
            <div className="border border-[#E5E7EB] rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5 text-[#007AFF]" />Attachments</span>
                <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => createFileRef.current?.click()} data-testid="create-attach-btn">
                  <Plus className="w-3 h-3 mr-1" />Add File
                </Button>
                <input type="file" ref={createFileRef} className="hidden" multiple
                  accept=".pdf,.doc,.docx,.txt,.md,.csv,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.zip"
                  onChange={(e) => { const files = Array.from(e.target.files || []); setCreateFiles(prev => [...prev, ...files]); e.target.value = ''; }}
                  data-testid="create-file-input" />
              </div>
              {createFiles.length > 0 && (
                <div className="space-y-1">
                  {createFiles.map((file, i) => {
                    const isImg = file.type?.startsWith('image/');
                    return (
                      <div key={i} className="flex items-center justify-between p-2 bg-[#F9FAFB] rounded-lg border border-[#E5E7EB]" data-testid={`create-file-${i}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          {isImg ? (
                            <img src={URL.createObjectURL(file)} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
                          ) : (
                            <FileIcon className="w-4 h-4 text-[#002FA7] flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="text-xs font-medium truncate">{file.name}</p>
                            <p className="text-[9px] text-[#9CA3AF]">{file.size >= 1024 ? (file.size / 1024).toFixed(0) + 'KB' : file.size + 'B'}</p>
                          </div>
                        </div>
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-[#FF3B30] flex-shrink-0"
                          onClick={() => setCreateFiles(prev => prev.filter((_, j) => j !== i))} data-testid={`remove-create-file-${i}`}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
              {createFiles.length === 0 && (
                <p className="text-[10px] text-[#9CA3AF] text-center py-1">No files attached. Click "Add File" to attach documents for review.</p>
              )}
            </div>
            {/* Recurring */}
            <div className="border border-[#E5E7EB] rounded-lg p-3 space-y-2">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="recurring-toggle-label">
                <input type="checkbox" checked={form.is_recurring} onChange={(e) => setForm({ ...form, is_recurring: e.target.checked, recurrence_frequency: e.target.checked ? 'weekly' : '' })} className="w-4 h-4 rounded border-[#D1D5DB]" data-testid="recurring-toggle" />
                <Repeat className="w-3.5 h-3.5 text-[#002FA7]" /><span className="text-sm font-medium">Recurring Task</span>
              </label>
              {form.is_recurring && (
                <div className="space-y-2 pl-6">
                  <Select value={form.recurrence_frequency} onValueChange={(v) => setForm({ ...form, recurrence_frequency: v })}>
                    <SelectTrigger data-testid="recurrence-frequency-select"><SelectValue placeholder="Frequency" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="date" value={form.recurrence_end_date} onChange={(e) => setForm({ ...form, recurrence_end_date: e.target.value })} placeholder="End date" data-testid="recurrence-end-date" />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsCreateOpen(false); setCreateFiles([]); }}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleCreate} data-testid="save-task-btn">Create Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Completion Summary Dialog */}
      <Dialog open={isCompleteOpen} onOpenChange={setIsCompleteOpen}>
        <DialogContent data-testid="complete-task-dialog">
          <DialogHeader><DialogTitle>Complete Task</DialogTitle></DialogHeader>
          <p className="text-sm text-[#4B5563] mb-2">Please summarize what was completed before marking this task as Done.</p>
          <Textarea placeholder="What was accomplished? Key outcomes..." value={completionSummary} onChange={(e) => setCompletionSummary(e.target.value)} rows={3} data-testid="completion-summary-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCompleteOpen(false)}>Cancel</Button>
            <Button className="bg-[#34C759] hover:bg-[#2DB84E] text-white" onClick={handleComplete} data-testid="confirm-complete-btn">
              <CheckCircle className="w-4 h-4 mr-1" />Mark as Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <input type="file" ref={fileInputRef} className="hidden" accept=".pdf,.doc,.docx,.txt,.md,.csv,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.zip" />

      {/* View Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-2xl" data-testid="view-task-dialog">
          <DialogHeader><DialogTitle>Task Details</DialogTitle></DialogHeader>
          {selectedTask && (
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <h3 className="text-lg font-semibold">{selectedTask.title}</h3>
                <div className="flex gap-2">
                  <Badge className={getPriorityBadge(selectedTask.priority)}>{selectedTask.priority}</Badge>
                  <Badge className={`${getStatusInfo(selectedTask.status).bg} text-white`}>{getStatusInfo(selectedTask.status).label}</Badge>
                  {riskBadge(selectedTask)}
                </div>
              </div>
              {selectedTask.description && <p className="text-sm text-[#4B5563]">{selectedTask.description}</p>}
              {selectedTask.suggested_action && (
                <div className="p-2 rounded-lg bg-[#FF9500]/5 border border-[#FF9500]/20">
                  <p className="text-xs text-[#FF9500] flex items-center gap-1"><Lightbulb className="w-3 h-3" /><strong>Suggested:</strong> {selectedTask.suggested_action}</p>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div><span className="text-[#9CA3AF] text-xs">Assignee</span><p className="font-medium text-xs">{getUserName(selectedTask.assigned_to)}</p></div>
                <div><span className="text-[#9CA3AF] text-xs">Due Date</span><p className="font-medium text-xs">{selectedTask.due_date ? format(new Date(selectedTask.due_date), 'MMM d, yyyy') : '-'}</p></div>
                <div><span className="text-[#9CA3AF] text-xs">Created</span><p className="font-medium text-xs">{format(new Date(selectedTask.created_at), 'MMM d, yyyy')}</p></div>
              </div>
              {/* Dependencies */}
              {selectedTask.depends_on?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold mb-1 flex items-center gap-1"><Link2 className="w-3 h-3 text-[#5856D6]" />Dependencies</h4>
                  <div className="space-y-1">
                    {selectedTask.depends_on.map((depId) => (
                      <div key={depId} className="flex items-center justify-between p-1.5 bg-[#F9FAFB] rounded text-xs">
                        <span>{depId}</span>
                        <Button variant="ghost" size="sm" className="h-5 text-[9px] text-[#FF3B30]" onClick={() => handleRemoveDep(selectedTask.task_id, depId)}>Remove</Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Blocker info */}
              {selectedTask.status === 'blocked' && (
                <div className="p-2 rounded-lg bg-[#FF3B30]/5 border border-[#FF3B30]/20">
                  <p className="text-xs font-semibold text-[#FF3B30] mb-0.5">Blocked</p>
                  {selectedTask.blocker_reason && <p className="text-xs text-[#4B5563]">Reason: {selectedTask.blocker_reason}</p>}
                  {selectedTask.blocked_by_person && <p className="text-xs text-[#4B5563]">Blocked by: {selectedTask.blocked_by_person}</p>}
                </div>
              )}
              {/* Completion summary */}
              {selectedTask.completion_summary && (
                <div className="p-2 rounded-lg bg-[#34C759]/5 border border-[#34C759]/20">
                  <p className="text-xs font-semibold text-[#34C759] mb-0.5">Completion Summary</p>
                  <p className="text-xs text-[#4B5563]">{selectedTask.completion_summary}</p>
                </div>
              )}
              {selectedTask.is_recurring && (
                <div className="flex items-center gap-2 p-2 bg-[#002FA7]/5 border border-[#002FA7]/20 rounded-lg" data-testid="recurring-info">
                  <Repeat className="w-3.5 h-3.5 text-[#002FA7]" />
                  <span className="text-xs text-[#002FA7] font-medium">Recurring — {selectedTask.recurrence_frequency}</span>
                </div>
              )}
              {/* Add dependency */}
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label className="text-xs">Add Dependency (Task ID)</Label>
                  <Select value={depInput || "none"} onValueChange={setDepInput}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select task..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select task...</SelectItem>
                      {tasks.filter(t => t.task_id !== selectedTask.task_id && !selectedTask.depends_on?.includes(t.task_id)).slice(0, 20).map(t => (
                        <SelectItem key={t.task_id} value={t.task_id}>{t.title.substring(0, 40)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button size="sm" className="h-8 bg-[#5856D6] hover:bg-[#4845B0]" onClick={() => { if (depInput && depInput !== 'none') { handleAddDep(selectedTask.task_id, depInput); setDepInput(''); } }} data-testid="add-dep-btn">
                  <Link2 className="w-3 h-3 mr-1" />Link
                </Button>
              </div>
              {/* Attachments */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-xs font-semibold">Attachments ({selectedTask.attachments?.length || 0})</h4>
                  <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => handleAttachToTask(selectedTask.task_id)} data-testid="task-attach-btn"><Paperclip className="w-3 h-3 mr-1" />Attach</Button>
                </div>
                {selectedTask.attachments?.map((att) => {
                  const isImage = att.content_type?.startsWith('image/');
                  const isPdf = att.content_type === 'application/pdf';
                  const isText = att.content_type?.startsWith('text/') || att.content_type === 'application/json';
                  const isViewable = isImage || isPdf || isText;
                  const viewUrl = `${API}/tasks/${selectedTask.task_id}/attachments/${att.file_id}/download`;
                  return (
                    <div key={att.file_id} className="p-2 bg-[#F9FAFB] rounded-lg border border-[#E5E7EB] mb-1.5" data-testid={`task-att-${att.file_id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <FileIcon className="w-3.5 h-3.5 text-[#002FA7] flex-shrink-0" />
                          <span className="text-xs font-medium truncate">{att.original_filename}</span>
                          <span className="text-[9px] text-[#9CA3AF] flex-shrink-0">{att.size >= 1024 ? (att.size / 1024).toFixed(0) + 'KB' : att.size + 'B'}</span>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {(isImage || isPdf) && (
                            <Button variant="ghost" size="sm" className="h-5 text-[9px] px-1.5 text-[#5856D6]"
                              onClick={() => { setReviewingAttachment(att); setIsViewOpen(false); }}
                              data-testid={`review-att-${att.file_id}`} title="Review & Annotate">
                              <MessageSquare className="w-3 h-3 mr-0.5" />Review
                            </Button>
                          )}
                          {isViewable && (
                            <Button variant="ghost" size="icon" className="h-5 w-5 text-[#002FA7]" onClick={() => window.open(viewUrl, '_blank')} data-testid={`view-att-${att.file_id}`} title="View"><Eye className="w-3 h-3" /></Button>
                          )}
                          <a href={viewUrl} download={att.original_filename} className="inline-flex items-center justify-center h-5 w-5 rounded-md hover:bg-accent text-[#4B5563]" data-testid={`download-att-${att.file_id}`} title="Download"><Download className="w-3 h-3" /></a>
                          <Button variant="ghost" size="icon" className="h-5 w-5 text-[#FF3B30]" onClick={() => handleRemoveAttachment(selectedTask.task_id, att.file_id)} title="Remove"><X className="w-3 h-3" /></Button>
                        </div>
                      </div>
                      {/* Inline preview for images */}
                      {isImage && (
                        <div className="mt-1.5 rounded overflow-hidden border border-[#E5E7EB] bg-white">
                          <img src={viewUrl} alt={att.original_filename} className="max-h-[200px] w-full object-contain" data-testid={`preview-img-${att.file_id}`} />
                        </div>
                      )}
                      {/* Inline preview for PDFs */}
                      {isPdf && (
                        <div className="mt-1.5 rounded overflow-hidden border border-[#E5E7EB]">
                          <iframe src={viewUrl} title={att.original_filename} className="w-full h-[300px]" data-testid={`preview-pdf-${att.file_id}`} />
                        </div>
                      )}
                      <p className="text-[9px] text-[#9CA3AF] mt-1">Uploaded by {att.uploaded_by_name} — {format(new Date(att.uploaded_at), 'MMM d, h:mm a')}</p>
                    </div>
                  );
                })}
              </div>
              {/* Notes */}
              {selectedTask.notes?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold mb-1">Notes</h4>
                  {selectedTask.notes.map((note, idx) => (
                    <div key={idx} className="p-2 bg-[#F3F4F6] text-xs mb-1 rounded">
                      <p>{note.content}</p>
                      <p className="text-[10px] text-[#9CA3AF] mt-0.5">{note.user_name} — {format(new Date(note.created_at), 'MMM d, h:mm a')}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg" data-testid="edit-task-dialog">
          <DialogHeader><DialogTitle>Edit Task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
            <div>
              <Label>Assign To</Label>
              <Select value={form.assigned_to || "none"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Select assignee" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Blocker fields when status is blocked */}
            {form.status === 'blocked' && (
              <div className="border border-[#FF3B30]/20 rounded-lg p-3 space-y-2 bg-[#FF3B30]/[0.02]">
                <p className="text-xs font-semibold text-[#FF3B30] flex items-center gap-1"><Ban className="w-3 h-3" />Blocker Details</p>
                <div><Label className="text-xs">Blocker Reason *</Label><Input value={form.blocker_reason} onChange={(e) => setForm({ ...form, blocker_reason: e.target.value })} placeholder="What is blocking this task?" data-testid="blocker-reason-input" /></div>
                <div>
                  <Label className="text-xs">Blocked By (Person/Team)</Label>
                  <Input value={form.blocked_by_person} onChange={(e) => setForm({ ...form, blocked_by_person: e.target.value })} placeholder="Who or what is blocking?" data-testid="blocked-by-input" />
                </div>
              </div>
            )}
            {/* Completion summary when status is done */}
            {form.status === 'done' && (
              <div className="border border-[#34C759]/20 rounded-lg p-3 bg-[#34C759]/[0.02]">
                <Label className="text-xs text-[#34C759] font-semibold">Completion Summary *</Label>
                <Textarea value={form.completion_summary || ''} onChange={(e) => setForm({ ...form, completion_summary: e.target.value })} placeholder="Summarize what was completed..." rows={2} data-testid="edit-completion-summary" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleUpdate} data-testid="update-task-btn">Update Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={isNoteOpen} onOpenChange={setIsNoteOpen}>
        <DialogContent data-testid="add-note-dialog">
          <DialogHeader><DialogTitle>Add Note to Task</DialogTitle></DialogHeader>
          <Textarea placeholder="Enter your note..." value={noteContent} onChange={(e) => setNoteContent(e.target.value)} data-testid="note-content-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNoteOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleAddNote} data-testid="save-note-btn">Add Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attachment Annotator Overlay */}
      {reviewingAttachment && selectedTask && (
        <AttachmentAnnotator
          taskId={selectedTask.task_id}
          attachment={reviewingAttachment}
          token={token}
          onClose={() => { setReviewingAttachment(null); setIsViewOpen(true); }}
        />
      )}
    </div>
  );
};

/* Extracted Kanban Column component for reuse in standard and swimlane modes */
const KanbanColumn = ({ col, colTasks, isOver, setDragOverCol, handleKanbanDrop, draggedTask, setDraggedTask, getPriorityBadge, getUserName, setSelectedTask, setIsViewOpen, compact }) => (
  <div
    className={`flex-shrink-0 rounded-xl transition-all ${compact ? 'w-[220px]' : 'w-[280px]'} ${isOver ? 'ring-2 ring-offset-2' : ''}`}
    style={{ ringColor: isOver ? col.color : undefined }}
    onDragOver={(e) => { e.preventDefault(); setDragOverCol(true); }}
    onDragLeave={() => setDragOverCol(null)}
    onDrop={(e) => { e.preventDefault(); const taskId = e.dataTransfer.getData('taskId'); if (taskId) handleKanbanDrop(taskId, col.value); }}
    data-testid={`kanban-col-${col.value}`}
  >
    <div className="flex items-center justify-between px-3 py-2 rounded-t-xl" style={{ backgroundColor: col.color + '12' }}>
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: col.color }} />
        <span className={`font-semibold text-[#0A0A0A] ${compact ? 'text-[10px]' : 'text-xs'}`}>{col.label}</span>
      </div>
      <Badge variant="secondary" className="text-[9px] h-4 min-w-[18px] justify-center">{colTasks.length}</Badge>
    </div>
    <div className={`p-1.5 space-y-1.5 rounded-b-xl border border-t-0 border-[#E5E7EB] transition-colors ${compact ? 'min-h-[80px]' : 'min-h-[120px]'} ${isOver ? 'bg-[#F0F4FF]' : 'bg-[#FAFAFA]'}`}>
      {colTasks.length === 0 ? (
        <div className={`flex items-center justify-center text-[9px] text-[#C4C4C4] select-none ${compact ? 'h-12' : 'h-20'}`}>Drop here</div>
      ) : colTasks.map((task) => (
        <div
          key={task.task_id}
          draggable
          onDragStart={(e) => { e.dataTransfer.setData('taskId', task.task_id); setDraggedTask(task.task_id); }}
          onDragEnd={() => { setDraggedTask(null); setDragOverCol(null); }}
          className={`group p-2 rounded-lg border bg-white hover:shadow-md transition-all cursor-grab active:cursor-grabbing ${draggedTask === task.task_id ? 'opacity-40 scale-95' : 'opacity-100'} ${task.is_overdue ? 'border-[#FF3B30]/40' : task.is_at_risk ? 'border-[#FF9500]/40' : 'border-[#E5E7EB]'}`}
          data-testid={`kanban-card-${task.task_id}`}
        >
          <div className="flex items-start justify-between gap-1 mb-1">
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <GripVertical className="w-2.5 h-2.5 text-[#C4C4C4]" />
            </div>
            <Badge className={`${getPriorityBadge(task.priority)} text-[7px] px-1`}>{task.priority}</Badge>
          </div>
          <button className={`font-medium text-[#0A0A0A] text-left leading-snug mb-1 hover:text-[#002FA7] transition-colors w-full ${compact ? 'text-[10px]' : 'text-xs'}`}
            onClick={() => { setSelectedTask(task); setIsViewOpen(true); }}>
            {task.title.length > (compact ? 40 : 60) ? task.title.substring(0, compact ? 40 : 60) + '...' : task.title}
          </button>
          {(task.is_overdue || task.is_at_risk) && (
            <div className="flex gap-1 mb-1">
              {task.is_overdue && <Badge className="text-[6px] bg-[#FF3B30]/10 text-[#FF3B30] px-1 py-0">OVERDUE</Badge>}
              {task.is_at_risk && !task.is_overdue && <Badge className="text-[6px] bg-[#FF9500]/10 text-[#FF9500] px-1 py-0">AT RISK</Badge>}
            </div>
          )}
          {task.status === 'blocked' && task.blocker_reason && (
            <p className="text-[8px] text-[#FF3B30] mb-1 truncate">{task.blocker_reason}</p>
          )}
          <div className="flex items-center justify-between pt-1 border-t border-[#F3F4F6]">
            <div className="flex items-center gap-1">
              {task.due_date && (
                <span className={`text-[8px] flex items-center gap-0.5 ${task.is_overdue ? 'text-[#FF3B30] font-medium' : 'text-[#9CA3AF]'}`}>
                  <Calendar className="w-2 h-2" />{format(new Date(task.due_date), 'MMM d')}
                </span>
              )}
              {task.depends_on?.length > 0 && <span className="text-[8px] text-[#5856D6] flex items-center gap-0.5"><Link2 className="w-2 h-2" />{task.depends_on.length}</span>}
            </div>
            <div className="flex items-center gap-0.5">
              {task.attachments?.length > 0 && <span className="text-[8px] text-[#002FA7] flex items-center gap-0.5"><Paperclip className="w-2 h-2" />{task.attachments.length}</span>}
              {task.notes?.length > 0 && <span className="text-[8px] text-[#9CA3AF]">{task.notes.length}<MessageSquare className="w-2 h-2 inline ml-0.5" /></span>}
              <div className="w-4 h-4 rounded-full bg-[#002FA7]/10 flex items-center justify-center" title={getUserName(task.assigned_to)}>
                <span className="text-[7px] font-bold text-[#002FA7]">{getUserName(task.assigned_to).charAt(0).toUpperCase()}</span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default TasksPage;
